import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Download, Loader2, CheckCircle2, Calendar, Building2, Stethoscope, FileText, MapPin, PackageX, FileSearch, Timer, Search, Contact } from 'lucide-react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Checkbox } from '../ui/Checkbox';
import { Badge } from '../ui/Badge';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/Card';
import { Report, ReportType } from '../../types';
import { MOCK_BRANCHES } from '../../utils/mockData';
interface ReportGenerateSheetProps {
  report: Report | null;
  isOpen: boolean;
  onClose: () => void;
}
export function ReportGenerateSheet({
  report,
  isOpen,
  onClose
}: ReportGenerateSheetProps) {
  const [generating, setGenerating] = useState(false);
  const [generated, setGenerated] = useState(false);
  const [selectedCities, setSelectedCities] = useState<string[]>([]);
  const [selectedBranches, setSelectedBranches] = useState<string[]>([]);
  const [format, setFormat] = useState<'CSV' | 'XLSX'>('XLSX');
  const [dateRange, setDateRange] = useState({
    start: '',
    end: ''
  });
  const [branchSearch, setBranchSearch] = useState('');
  // Group branches by city
  const citiesMap = MOCK_BRANCHES.reduce((acc, branch) => {
    if (!acc[branch.city]) acc[branch.city] = [];
    acc[branch.city].push(branch);
    return acc;
  }, {} as Record<string, typeof MOCK_BRANCHES>);
  const allCities = Object.keys(citiesMap);
  // Branches to display: filtered by selected cities, or all if none selected
  const visibleBranches = (selectedCities.length > 0 ? MOCK_BRANCHES.filter(b => selectedCities.includes(b.city)) : MOCK_BRANCHES).filter(b => {
    if (!branchSearch) return true;
    const q = branchSearch.toLowerCase();
    return b.code.toLowerCase().includes(q) || b.city.toLowerCase().includes(q) || b.name.toLowerCase().includes(q);
  });
  const toggleCity = (city: string) => {
    const branchIds = (citiesMap[city] || []).map(b => b.id);
    setSelectedCities(prev => {
      const isSelected = prev.includes(city);
      if (isSelected) {
        // Deselect city and its branches
        setSelectedBranches(prevBranches => prevBranches.filter(id => !branchIds.includes(id)));
        return prev.filter(c => c !== city);
      } else {
        // Select city and all its branches
        setSelectedBranches(prevBranches => [...new Set([...prevBranches, ...branchIds])]);
        return [...prev, city];
      }
    });
  };
  const toggleBranch = (branchId: string) => {
    setSelectedBranches(prev => prev.includes(branchId) ? prev.filter(id => id !== branchId) : [...prev, branchId]);
  };
  const getReportIcon = (type: ReportType) => {
    switch (type) {
      case 'PRESCRIPTION_STATUS':
        return <Stethoscope className="w-5 h-5 text-teal-600" data-id="element-190" />;
      case 'DETAILED_PRESCRIPTION':
        return <FileSearch className="w-5 h-5 text-indigo-600" data-id="element-191" />;
      case 'SLA_REPORT':
        return <Timer className="w-5 h-5 text-blue-600" data-id="element-192" />;
      case 'UNAVAILABLE_PRODUCTS':
        return <PackageX className="w-5 h-5 text-amber-600" data-id="element-193" />;
      case 'CUSTOMER_DETAILS':
        return <Contact className="w-5 h-5 text-violet-600" data-id="element-194" />;
      default:
        return <FileText className="w-5 h-5 text-slate-600" data-id="element-195" />;
    }
  };
  const getIconBg = (type: ReportType) => {
    switch (type) {
      case 'PRESCRIPTION_STATUS':
        return 'bg-teal-50';
      case 'DETAILED_PRESCRIPTION':
        return 'bg-indigo-50';
      case 'SLA_REPORT':
        return 'bg-blue-50';
      case 'UNAVAILABLE_PRODUCTS':
        return 'bg-amber-50';
      case 'CUSTOMER_DETAILS':
        return 'bg-violet-50';
      default:
        return 'bg-slate-100';
    }
  };
  const handleGenerate = () => {
    setGenerating(true);
    setTimeout(() => {
      setGenerating(false);
      setGenerated(true);
      // Auto-close after success
      setTimeout(() => {
        onClose();
        // Reset state after close animation
        setTimeout(() => {
          setGenerated(false);
          setSelectedCities([]);
          setSelectedBranches([]);
          setDateRange({
            start: '',
            end: ''
          });
          setFormat('XLSX');
        }, 300);
      }, 1500);
    }, 2000);
  };
  const handleClose = () => {
    if (!generating) {
      onClose();
      // Reset state after close
      setTimeout(() => {
        setGenerated(false);
        setSelectedCities([]);
        setSelectedBranches([]);
        setBranchSearch('');
        setDateRange({
          start: '',
          end: ''
        });
        setFormat('XLSX');
      }, 300);
    }
  };
  return <AnimatePresence data-id="element-196">
      {isOpen && report && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 bg-black/50 z-40 backdrop-blur-sm" onClick={handleClose} data-id="element-197" />

          {/* Sheet */}
          <motion.div initial={{
        x: '100%'
      }} animate={{
        x: 0
      }} exit={{
        x: '100%'
      }} transition={{
        type: 'spring',
        stiffness: 300,
        damping: 30
      }} className="fixed right-0 top-0 bottom-0 w-full max-w-lg bg-white shadow-2xl z-50 flex flex-col" data-id="element-198">
            {/* Header */}
            <div className="flex-shrink-0 border-b border-slate-200 bg-white" data-id="element-199">
              <div className="px-6 py-4 flex items-start justify-between" data-id="element-200">
                <div className="flex items-start gap-4" data-id="element-201">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${getIconBg(report.type as ReportType)}`} data-id="element-202">
                    {getReportIcon(report.type as ReportType)}
                  </div>
                  <div data-id="element-203">
                    <h2 className="text-lg font-bold text-slate-900" data-id="element-204">
                      {report.name}
                    </h2>
                    <p className="text-sm text-slate-500 mt-0.5 line-clamp-2" data-id="element-205">
                      {report.description}
                    </p>
                  </div>
                </div>
                <button onClick={handleClose} disabled={generating} className="p-2 hover:bg-slate-100 rounded-full transition-colors disabled:opacity-50" title="Close" data-id="element-206">
                  <X className="w-5 h-5 text-slate-500" data-id="element-207" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6 bg-slate-50/50 space-y-6" data-id="element-208">
              {/* Success Message */}
              {generated && <motion.div initial={{
            opacity: 0,
            y: -10
          }} animate={{
            opacity: 1,
            y: 0
          }} className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg" data-id="element-209">
                  <div className="flex items-center gap-2 text-emerald-700" data-id="element-210">
                    <CheckCircle2 className="w-5 h-5" data-id="element-211" />
                    <span className="font-medium" data-id="element-212">
                      Report generated! Download starting...
                    </span>
                  </div>
                </motion.div>}

              {/* Date Range */}
              <Card data-id="element-213">
                <CardHeader className="pb-3" data-id="element-214">
                  <CardTitle className="text-sm flex items-center gap-2" data-id="element-215">
                    <Calendar className="w-4 h-4 text-slate-500" data-id="element-216" />
                    Date Range
                  </CardTitle>
                </CardHeader>
                <CardContent data-id="element-217">
                  <div className="grid grid-cols-2 gap-4 mb-3" data-id="element-218">
                    <Input label="From" type="date" value={dateRange.start} onChange={e => setDateRange({
                  ...dateRange,
                  start: e.target.value
                })} data-id="element-219" />
                    <Input label="To" type="date" value={dateRange.end} onChange={e => setDateRange({
                  ...dateRange,
                  end: e.target.value
                })} data-id="element-220" />
                  </div>
                </CardContent>
              </Card>

              {/* City Selection */}
              <Card data-id="element-221">
                <CardHeader className="pb-3" data-id="element-222">
                  <CardTitle className="text-sm flex items-center gap-2" data-id="element-223">
                    <MapPin className="w-4 h-4 text-slate-500" data-id="element-224" />
                    Cities
                    {selectedCities.length > 0 && <Badge variant="info" size="sm" data-id="element-225">
                        {selectedCities.length} selected
                      </Badge>}
                  </CardTitle>
                </CardHeader>
                <CardContent data-id="element-226">
                  <div className="flex flex-wrap gap-2" data-id="element-227">
                    {allCities.map(city => {
                  const isSelected = selectedCities.includes(city);
                  const branchCount = citiesMap[city]?.length || 0;
                  return <button key={city} type="button" onClick={() => toggleCity(city)} className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium border-2 transition-all ${isSelected ? 'border-teal-600 bg-teal-50 text-teal-700' : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'}`} data-id="element-228">
                          <MapPin className="w-3.5 h-3.5" data-id="element-229" />
                          {city}
                          <span className={`text-xs ${isSelected ? 'text-teal-500' : 'text-slate-400'}`} data-id="element-230">
                            ({branchCount})
                          </span>
                        </button>;
                })}
                  </div>
                </CardContent>
              </Card>

              {/* Branch Selection */}
              <Card data-id="element-231">
                <CardHeader className="pb-3" data-id="element-232">
                  <div className="flex items-center justify-between" data-id="element-233">
                    <CardTitle className="text-sm flex items-center gap-2" data-id="element-234">
                      <Building2 className="w-4 h-4 text-slate-500" data-id="element-235" />
                      Branches
                      {selectedBranches.length > 0 && <Badge variant="info" size="sm" data-id="element-236">
                          {selectedBranches.length} selected
                        </Badge>}
                    </CardTitle>
                  </div>
                </CardHeader>
                <CardContent data-id="element-237">
                  <div className="relative mb-3" data-id="element-238">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" data-id="element-239" />
                    <input type="text" placeholder="Search by branch code or city..." className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500" value={branchSearch} onChange={e => setBranchSearch(e.target.value)} data-id="element-240" />
                  </div>
                  <div className="space-y-2 max-h-48 overflow-y-auto" data-id="element-241">
                    {visibleBranches.map(branch => <label key={branch.id} className="flex items-center gap-3 p-2.5 border border-slate-200 rounded-lg hover:bg-slate-50 cursor-pointer transition-colors" data-id="element-242">
                        <Checkbox checked={selectedBranches.includes(branch.id)} onCheckedChange={() => toggleBranch(branch.id)} data-id="element-243" />
                        <div className="flex-1 min-w-0" data-id="element-244">
                          <div className="font-mono font-medium text-sm text-slate-900 truncate" data-id="element-245">
                            {branch.code}
                          </div>
                          <div className="text-xs text-slate-500" data-id="element-246">
                            {branch.city}
                          </div>
                        </div>
                        <Badge variant={branch.status === 'ACTIVE' ? 'success' : 'neutral'} size="sm" data-id="element-247">
                          {branch.status}
                        </Badge>
                      </label>)}
                  </div>
                </CardContent>
              </Card>

              {/* Format Selection */}
              <Card data-id="element-248">
                <CardHeader className="pb-3" data-id="element-249">
                  <CardTitle className="text-sm flex items-center gap-2" data-id="element-250">
                    <FileText className="w-4 h-4 text-slate-500" data-id="element-251" />
                    Export Format
                  </CardTitle>
                </CardHeader>
                <CardContent data-id="element-252">
                  <div className="flex gap-3" data-id="element-253">
                    <label className={`flex-1 flex items-center justify-center gap-2 p-3 border-2 rounded-lg cursor-pointer transition-all ${format === 'XLSX' ? 'border-teal-600 bg-teal-50 text-teal-700' : 'border-slate-200 hover:border-slate-300 text-slate-600'}`} data-id="element-254">
                      <input type="radio" name="format" value="XLSX" checked={format === 'XLSX'} onChange={e => setFormat(e.target.value as any)} className="sr-only" data-id="element-255" />
                      <span className="text-sm font-medium" data-id="element-256">Excel</span>
                    </label>
                    <label className={`flex-1 flex items-center justify-center gap-2 p-3 border-2 rounded-lg cursor-pointer transition-all ${format === 'CSV' ? 'border-teal-600 bg-teal-50 text-teal-700' : 'border-slate-200 hover:border-slate-300 text-slate-600'}`} data-id="element-257">
                      <input type="radio" name="format" value="CSV" checked={format === 'CSV'} onChange={e => setFormat(e.target.value as any)} className="sr-only" data-id="element-258" />
                      <span className="text-sm font-medium" data-id="element-259">CSV</span>
                    </label>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Footer */}
            <div className="flex-shrink-0 border-t border-slate-200 bg-white p-4" data-id="element-260">
              <Button className="w-full bg-teal-600 hover:bg-teal-700" onClick={handleGenerate} disabled={generating || generated} data-id="element-261">
                {generating ? <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" data-id="element-262" />
                    Generating...
                  </> : generated ? <>
                    <CheckCircle2 className="w-4 h-4 mr-2" data-id="element-263" />
                    Generated!
                  </> : <>
                    <Download className="w-4 h-4 mr-2" data-id="element-264" />
                    Generate Report
                  </>}
              </Button>
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}